import type { EChartsOption } from 'echarts'
import { useEffect, useMemo, useState } from 'react'
import { axisStyle, baseOption, Chart } from '../../components/Chart'
import { DataTable, ErrorBox, Panel, ScreenHeading, Signed, Spinner } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n'
import { monthLabel, pct, ppiLabel } from '../../lib/format'

interface TeamMonth {
  month: string
  quotes: number
  avgGapPct: number | null
  withinShare: number | null
  escalated: number
  approved: number
  rejected: number
  won: number
  lost: number
  realisedGapPct: number | null
  ppi: number | null
  quotedPpi: number | null
}
interface TeamSummary {
  team: string
  quotes: number
  avgGapPct: number | null
  withinShare: number | null
  escalated: number
  approved: number
  rejected: number
  won: number
  lost: number
  realisedGapPct: number | null
  ppi: number | null
  quotedPpi: number | null
  pending: number
}
interface TeamRep {
  userId: string
  name: string
  team: string
  quotes: number
  avgGapPct: number | null
  withinShare: number | null
  escalated: number
  approved: number
  won: number
  lost: number
  realisedGapPct: number | null
  ppi: number | null
  quotedPpi: number | null
}
interface Team {
  window: { from: string; to: string }
  months: TeamMonth[]
  reps: TeamRep[]
  teams: TeamSummary[]
  pending: number
  total: { ppi: number | null; quotedPpi: number | null; won: number }
}

/** §8.4 — realised price against target over time, and the share inside the concession band. */
export function TeamView() {
  const { user } = useAuth()
  const { t: tr, lang } = useT()
  const [t, setT] = useState<Team | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    api.get<Team>('/api/team?months=6').then(setT).catch((e) => setError(errorMessage(e)))
  }, [])

  const build = useMemo(
    () => (c: Parameters<typeof baseOption>[0]): EChartsOption => ({
      ...baseOption(c),
      legend: { top: 0, textStyle: { color: c.text, fontSize: 11 }, itemWidth: 10, itemHeight: 8 },
      xAxis: { type: 'category' as const, data: t?.months.map((m) => monthLabel(m.month)) ?? [], ...axisStyle(c), splitLine: { show: false } },
      yAxis: { type: 'value' as const, scale: true, ...axisStyle(c), axisLabel: { color: c.text, fontSize: 11 } },
      series: [
        {
          name: tr('PPI'),
          type: 'line' as const,
          connectNulls: true,
          data: t?.months.map((m) => (m.ppi === null ? null : Math.round(m.ppi * 10) / 10)) ?? [],
          itemStyle: { color: c.accent },
          lineStyle: { color: c.accent, width: 2.5 },
          symbolSize: 6,
          markLine: { silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: c.text, type: 'dashed', width: 1, opacity: 0.6 }, data: [{ yAxis: 100 }] },
        },
        {
          name: tr('Quoted PPI'),
          type: 'line' as const,
          connectNulls: true,
          data: t?.months.map((m) => (m.quotedPpi === null ? null : Math.round(m.quotedPpi * 10) / 10)) ?? [],
          itemStyle: { color: c.text },
          lineStyle: { color: c.text, width: 1.5, type: 'dotted' },
          symbolSize: 4,
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang],
  )

  if (error) return <ErrorBox>{error}</ErrorBox>
  if (!t) return <Spinner />
  const totalQ = t.months.reduce((s, m) => s + m.quotes, 0)
  const esc = t.months.reduce((s, m) => s + m.escalated, 0)
  const won = t.months.reduce((s, m) => s + m.won, 0)
  const lost = t.months.reduce((s, m) => s + m.lost, 0)
  const head = user?.role === 'head'
  const ppiCell = (v: number | null) => (v === null ? '–' : <Signed value={v - 100}>{ppiLabel(v)}</Signed>)
  const tone = (v: number | null) => (v === null ? undefined : v < 100 ? ('neg' as const) : ('pos' as const))
  return (
    <div>
      <ScreenHeading
        title={head ? tr('All teams') : `${tr('Team')} ${user?.salesTeam}`}
        subtitle={`${monthLabel(t.window.from)} – ${monthLabel(t.window.to)} · ${tr('quoted and realised prices against target')}`}
        stats={[
          { label: tr('PPI'), value: ppiLabel(t.total.ppi), tone: tone(t.total.ppi), foot: tr('final price ÷ target × 100, won quotes') },
          { label: tr('Quoted PPI'), value: ppiLabel(t.total.quotedPpi), tone: tone(t.total.quotedPpi), foot: tr('quoted or approved price') },
          { label: tr('Quotes'), value: totalQ, foot: tr('{n} pending approval', { n: t.pending }) },
          { label: tr('Escalated'), value: esc, foot: totalQ ? `${pct(esc / totalQ, 0)} ${tr('of quotes')}` : '' },
          { label: tr('Won / lost'), value: `${won} / ${lost}`, foot: won + lost ? `${pct(won / (won + lost), 0)} ${tr('win rate on decided')}` : tr('no outcomes yet') },
        ]}
      />
      {head && (
        <Panel title={tr('By team')} className="mb-4">
          <DataTable
            rows={t.teams}
            rowKey={(r) => r.team}
            columns={[
              { key: 't', header: tr('Team'), render: (r) => <span className="font-medium text-slate-100">{r.team}</span> },
              { key: 'rg', header: tr('PPI'), align: 'right', render: (r) => <span className="font-semibold">{ppiCell(r.ppi)}</span> },
              { key: 'g', header: tr('Quoted PPI'), align: 'right', render: (r) => ppiCell(r.quotedPpi) },
              { key: 'q', header: tr('Quotes'), align: 'right', render: (r) => r.quotes },
              { key: 'w', header: tr('Inside band'), align: 'right', render: (r) => pct(r.withinShare, 0) },
              { key: 'e', header: tr('Escalated'), align: 'right', render: (r) => (r.quotes ? `${r.escalated} · ${pct(r.escalated / r.quotes, 0)}` : r.escalated) },
              { key: 'a', header: tr('Approved / rejected'), align: 'right', render: (r) => `${r.approved} / ${r.rejected}` },
              { key: 'p', header: tr('Pending'), align: 'right', render: (r) => (r.pending ? <span className="font-semibold text-amber-500">{r.pending}</span> : 0) },
              { key: 'wl', header: tr('Won / lost'), align: 'right', render: (r) => `${r.won} / ${r.lost}` },
            ]}
          />
        </Panel>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={tr('PPI by month')}>
          <Chart build={build} height={240} />
        </Panel>
        <Panel title={tr('By rep')}>
          <DataTable
            rows={[...t.reps].sort((a, b) => (head ? a.team.localeCompare(b.team) : 0) || (b.ppi ?? -1) - (a.ppi ?? -1))}
            rowKey={(r) => r.userId}
            maxHeight="22rem"
            columns={[
              { key: 'n', header: tr('Rep'), render: (r) => <span className="text-slate-200">{r.name}</span> },
              ...(head ? [{ key: 'tm', header: tr('Team'), render: (r: TeamRep) => <span className="text-slate-500">{r.team}</span> }] : []),
              { key: 'rg', header: tr('PPI'), align: 'right', render: (r) => <span className="font-semibold">{ppiCell(r.ppi)}</span> },
              { key: 'g', header: tr('Quoted PPI'), align: 'right', render: (r) => ppiCell(r.quotedPpi) },
              { key: 'q', header: tr('Quotes'), align: 'right', render: (r) => r.quotes },
              { key: 'e', header: tr('Escalated'), align: 'right', render: (r) => r.escalated },
              { key: 'wl', header: tr('Won / lost'), align: 'right', render: (r) => `${r.won} / ${r.lost}` },
            ]}
          />
        </Panel>
      </div>
    </div>
  )
}
